/**
 * API Client — Centralized HTTP client with interceptors
 *
 * Handles all API communication for the application.
 * - Automatically attaches JWT token from localStorage to every request
 * - Sends accept-language header based on user's language preference
 * - Sends x-csrf-token header (double-submit cookie pattern)
 * - Supports JSON and FormData request bodies
 * - Includes request timeout with AbortController (default: 5 min)
 * - Normalizes all responses into a consistent { ok, status, data, message } shape
 * - Auto-refreshes JWT on 401 using refresh token cookie, then retries the request
 */

import { forceLogout } from "@/lib/auth";
import { isLoggingOut } from "@/lib/logout-state";

const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;

// Shared refresh promise — ensures only 1 refresh call fires even if multiple requests get 401
let refreshPromise = null;

/** Decode JWT payload (base64url → JSON) without external library. */
function decodeJwtPayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/** Returns true if token is expired or will expire within 60 seconds. */
function isTokenExpiringSoon(token) {
  if (!token) return true;
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return payload.exp - Math.floor(Date.now() / 1000) < 60;
}

/** Read CSRF token — prefer localStorage (works cross-origin), fall back to cookie (same-origin dev). */
function getCsrfToken() {
  if (typeof window === "undefined") return undefined;
  return (
    localStorage.getItem("csrfToken") ||
    document.cookie
      .split("; ")
      .find((c) => c.startsWith("csrf-token="))
      ?.split("=")[1]
  );
}

/**
 * Intercept and modify request before sending
 */
async function handleRequestInterceptors(url, options = {}) {
  let token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;

  // Proactive refresh: if token is expiring within 60 seconds, refresh before request
  const isAuthUrl = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/set-password");
  if (token && !isAuthUrl && isTokenExpiringSoon(token) && !isLoggingOut()) {
    if (!refreshPromise) {
      refreshPromise = tryRefreshToken().finally(() => { refreshPromise = null; });
    }
    await refreshPromise;
    token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  }

  // Get language from localStorage, default to "en"
  const language =
    typeof window !== "undefined"
      ? localStorage.getItem("language") || "en"
      : "en";

  // Don't set Content-Type for FormData - browser will set it with boundary
  const isFormData = options.body instanceof FormData;

  const csrfToken = getCsrfToken();

  const headers = {
    ...(!isFormData && { "Content-Type": "application/json" }),
    Accept: "application/json",
    "accept-language": language,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(csrfToken && { "x-csrf-token": csrfToken }),
    ...options.headers,
  };

  return {
    ...options,
    headers,
  };
}

/**
 * Intercept and normalize response before returning
 */
async function handleResponseInterceptors(response) {
  const contentType = response.headers.get("content-type");
  const isJSON = contentType && contentType.includes("application/json");
  const data = isJSON
    ? await response.json().catch(() => ({}))
    : await response.text();

  if (!response.ok) {
    // Return a standardized error object instead of throwing UI-specific notifications
    return {
      ok: false,
      status: response.status,
      error: true,
      message: data?.message || "API request failed",
      data,
    };
  }

  return {
    ok: true,
    status: response.status,
    data,
  };
}

/**
 * Attempt to refresh the access token using the HttpOnly refresh token cookie.
 * Returns true if refresh succeeded, false otherwise.
 */
async function tryRefreshToken() {
  try {
    const csrfToken = getCsrfToken();
    const refreshUrl = `${BASE_URL || ""}/api/v1/auth/refresh`;

    const response = await fetch(refreshUrl, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(csrfToken && { "x-csrf-token": csrfToken }),
      },
    });

    if (!response.ok) return false;

    const result = await response.json();
    const accessToken = result?.data?.accessToken;
    const newCsrfToken = result?.data?.csrfToken;
    if (accessToken) {
      localStorage.setItem("token", accessToken);
      if (newCsrfToken) localStorage.setItem("csrfToken", newCsrfToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Unified API client with request/response interceptors
 */
export async function apiClient(url, options = {}) {
  const finalOptions = await handleRequestInterceptors(url, options);
  const controller = new AbortController();

  // Use timeout from options if provided, otherwise default to 5 minutes
  // CRITICAL: This allows long-running operations like checkout with accounting integration
  const timeoutDuration = options.timeout || 300000;

  const timeout = setTimeout(() => controller.abort(), timeoutDuration);

  try {
    // Check if URL is absolute (starts with http:// or https://)
    const isAbsoluteUrl =
      url.startsWith("http://") || url.startsWith("https://");
    const finalUrl = isAbsoluteUrl ? url : `${BASE_URL}${url}`;
    const response = await fetch(finalUrl, {
      ...finalOptions,
      credentials: "include",
      signal: controller.signal,
    });

    clearTimeout(timeout);

    // On 401: try refreshing the JWT, then retry the original request
    // Only attempt refresh if user was logged in (has token in localStorage)
    const hadToken =
      typeof window !== "undefined" && !!localStorage.getItem("token");
    if (
      response.status === 401 &&
      hadToken &&
      !url.includes("/auth/refresh") &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/set-password") &&
      !isLoggingOut()
    ) {
      if (!refreshPromise) {
        refreshPromise = tryRefreshToken().finally(() => {
          refreshPromise = null;
        });
      }
      const refreshed = await refreshPromise;
      if (refreshed) {
        return apiClient(url, options);
      }
      // Both tokens expired — force logout
      forceLogout();
      return {
        ok: false,
        status: 401,
        error: true,
        data: null,
        message: "Session expired",
      };
    }

    return await handleResponseInterceptors(response);
  } catch (err) {
    clearTimeout(timeout);
    // Handle network / timeout / abort errors
    return {
      ok: false,
      error: true,
      status: err.name === "AbortError" ? 408 : 500,
      message: err.message || "Network error",
      data: null,
    };
  }
}
